<script setup>
import { computed, onMounted, ref } from 'vue';

const props = defineProps({
  playbackId: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: 'Livestream',
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  muted: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

// The player element drags in hls.js and the media-chrome suite — around 1MB that only a live
// Mux stream ever needs, so it is fetched on mount rather than bundled into the app entry.
//
// Gating a plain element on a ref, rather than reaching for defineAsyncComponent, is
// deliberate: the async-component placeholder is a fragment, and swapping it out mid-mount
// crashed the bundled build in removeFragment() with a null anchor. A boolean has no such
// placeholder. The wrapper below stays put either way, so it holds the 16/9 box and the
// layout does not shift while the chunk arrives.
const isPlayerElementReady = ref(false);

onMounted(async () => {
  await import('@mux/mux-player');

  isPlayerElementReady.value = true;
});

const playerAttributes = computed(() => ({
  'playback-id': props.playbackId,
  'stream-type': 'live',
  'metadata-video-title': props.title,
  autoplay: props.autoplay ? '' : undefined,
  muted: props.muted ? '' : undefined,
  playsinline: '',
}));
</script>

<template>
  <div class="mux-livestream-player" :class="{ 'mux-livestream-player--compact': compact }">
    <mux-player v-if="isPlayerElementReady" v-bind="playerAttributes"></mux-player>
  </div>
</template>
